import React, { createContext, useContext, useState, useEffect } from 'react'

type Language = 'en' | 'cy'

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  t: (key: string) => string
}

const translations = {
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.packages': 'Packages',
    'nav.switch': 'Switch',
    'nav.checkServices': 'Check Coverage',
    'nav.contact': 'Contact',
    'nav.support': 'Support',
    'nav.about': 'ABOUT',
    'nav.signUp': 'SIGN UP',
    'nav.networkUsage': 'Network',
    'nav.companyName': 'Direct Connect Wales',

    // Hero Section
    'hero.tagline': 'Proudly serving Wales',
    'hero.title': 'Full Fibre Broadband for Wales',
    'hero.subtitle': 'Wales-owned broadband, built for Welsh homes and businesses.',
    'hero.checkCoverage': 'Check coverage',
    'hero.viewPackages': 'View packages',
    'hero.speed': 'Up to 1 Gbps',
    'hero.speedDesc': 'Full fibre, unlimited use',
    'hero.noThrottling': 'No throttling',
    'hero.alwaysFast': 'Always fast',
    'hero.support': 'Local support',
    'hero.expertHelp': 'Based in Ebbw Vale',
    'hero.checkAvailability': 'Check availability',
    'hero.enterPostcode': 'Enter your postcode to see if we\'re available in your area',
    'hero.postcodeLabel': 'Enter your postcode',
    'hero.noRises': 'No mid-contract rises',
    'hero.checkNow': 'Check now',
    'hero.seePlans': 'See plans',
    'hero.setupTime': 'Install visit usually under 30 minutes',

    // Plans Section
    'plans.broadbandPlans': 'BROADBAND PLANS',
    'plans.chooseTitle': 'Choose the perfect plan for you',
    'plans.title': 'Choose the perfect plan for you',
    'plans.subtitle': 'Straightforward broadband for Welsh homes and businesses. No hidden fees, no jargon \u2014 just fast, reliable internet.',
    'plans.home': 'Home',
    'plans.business': 'Business',
    'plans.mostPopular': 'Most popular',
    'plans.select': 'SELECT',
    'plans.includesRouter': 'Includes Wi‑Fi router',
    'plans.errorTitle': 'Unable to load packages',
    'plans.errorNetwork': 'Please check your internet connection and try again.',
    'plans.tryAgain': 'Try Again',
    'plans.noPackages': 'No packages available at the moment.',

    // CTAs
    'cta.checkPostcode': 'Check if this plan is available in your postcode',
    'cta.upgradeSpeed': 'Upgrade your speed in minutes',
    'cta.speakPerson': 'Speak to a real person',
    'cta.switchNow': 'Switch now',
    'cta.comparePlans': 'Compare plans',
    'cta.getStarted': 'Get started',

    // Support
    'support.title': 'We\'re here to help',
    'support.phone': 'Phone',
    'support.email': 'Email',
    'support.liveChat': 'Live Chat',
    'support.available247': 'Mon–Fri, 9am–5pm',

    // Footer
    'footer.followUs': 'Follow us',
    'footer.stayConnected': 'Stay connected with us on social media',
    'footer.allRightsReserved': 'All rights reserved',

    // Switching Journey
    'switch.title': 'Switch to Direct Connect Wales',
    'switch.subtitle': 'Making the switch is easy and worry-free',
    'switch.step1': 'Check availability',
    'switch.step2': 'Choose a plan',
    'switch.step3': 'We handle the switch',
    'switch.step4': 'Get connected',
    'switch.noInterruption': 'No Service Interruption',
    'switch.4gConnection': 'We time the switch to keep any gap as short as possible',
    'switch.weHandleEverything': 'We will handle everything',
    'switch.supportAvailable': 'Support Mon–Fri, 9am–5pm',

    // Coverage Section
    'coverage.title': 'COVERAGE',
    'coverage.heading': 'Expanding across Wales',
    'coverage.description': 'We\'re continuously expanding our full-fibre broadband network across Wales. Check your postcode to see if we\'re available in your area, or register your interest for future coverage.',
    'coverage.postcodePlaceholder': 'Enter your postcode',
    'coverage.checkButton': 'Check Coverage',
    'coverage.mapTitle': 'Interactive Map',
    'coverage.mapComingSoon': 'Live coverage visualization coming soon!',

    // Testimonials Section
    'testimonials.title': 'TESTIMONIALS',
    'testimonials.heading': 'Trusted by Wales',
    'testimonials.subtitle': 'See what our customers have to say about their experience with Direct Connect Wales',

    // Help Section
    'help.title': 'HELP',
    'help.heading': 'We\'re here to help',
    'help.description': 'Get instant answers to common questions, check service status, or connect with our support team.',
    'help.setupGuides': 'Setup guides',
    'help.outageStatus': 'Outage status',
    'help.speedTest': 'Run a speed test',
    'help.contactSupport': 'Contact support',
    'help.needHelpNow': 'Need a hand now?',
    'help.chatOrCallback': 'Chat with a specialist or book a callback.',
    'help.liveChat': 'Live Chat',
    'help.bookCall': 'Book a Call',

    // Packages Page
    'packagesPage.label': 'PACKAGES',
    'packagesPage.title': 'Choose Your Broadband Package',
    'packagesPage.description': 'Fast, reliable internet solutions for homes and businesses across Wales.',
  },
  cy: {
    // Navigation
    'nav.home': 'Cartref',
    'nav.packages': 'Pecynnau',
    'nav.switch': 'Newid',
    'nav.checkServices': 'Gwirio Cwmpas',
    'nav.contact': 'Cysylltu',
    'nav.support': 'Cymorth',
    'nav.about': 'AMDANOM',
    'nav.signUp': 'COFRESTRU',
    'nav.networkUsage': 'Rhwydwaith',
    'nav.companyName': 'Direct Connect Cymru',

    // Hero Section
    'hero.tagline': 'Yn falch o wasanaethu Cymru',
    'hero.title': 'Band Eang Ffeibr Llawn i Gymru',
    'hero.subtitle': 'Band eang sy\'n eiddo i Gymru, wedi\'i adeiladu ar gyfer cartrefi a busnesau Cymreig.',
    'hero.checkCoverage': 'Gwirio cwmpas',
    'hero.viewPackages': 'Gweld pecynnau',
    'hero.speed': 'Hyd at 1 Gbps',
    'hero.speedDesc': 'Ffeibr llawn, defnydd diderfyn',
    'hero.noThrottling': 'Dim tagio',
    'hero.alwaysFast': 'Cyflym bob amser',
    'hero.support': 'Cymorth lleol',
    'hero.expertHelp': 'Tîm yng Nghymru',
    'hero.noRises': 'Dim codiadau canol contract',
    'hero.checkAvailability': 'Gwirio argaeledd',
    'hero.enterPostcode': 'Rhowch eich cod post i weld a ydym ar gael yn eich ardal',
    'hero.postcodeLabel': 'Rhowch god post',
    'hero.checkNow': 'Gwirio nawr',
    'hero.seePlans': 'Gweld cynlluniau',
    'hero.setupTime': 'Amser gosod cyfartalog: o dan 30 munud',

    // Plans Section
    'plans.broadbandPlans': 'CYNLLUNIAU BAND EANG',
    'plans.chooseTitle': 'Dewiswch y cynllun perffaith i chi',
    'plans.title': 'Dewiswch y cynllun perffaith i chi',
    'plans.subtitle': 'Band eang syml ar gyfer cartrefi a busnesau Cymreig. Dim ffioedd cudd, dim jargon \u2014 dim ond rhyngrwyd cyflym, dibynadwy.',
    'plans.home': 'Cartref',
    'plans.business': 'Busnes',
    'plans.mostPopular': 'Mwyaf poblogaidd',
    'plans.select': 'DEWIS',
    'plans.includesRouter': 'Yn cynnwys llwybrydd Wi-Fi',
    'plans.errorTitle': 'Methu llwytho pecynnau',
    'plans.errorNetwork': 'Gwiriwch eich cysylltiad rhyngrwyd a rhowch gynnig arall arni.',
    'plans.tryAgain': 'Rhowch Gynnig Arall',
    'plans.noPackages': 'Dim pecynnau ar gael ar hyn o bryd.',

    // CTAs
    'cta.checkPostcode': 'Gwiriwch a yw\'r cynllun hwn ar gael yn eich cod post',
    'cta.upgradeSpeed': 'Uwchraddio eich cyflymder mewn munudau',
    'cta.speakPerson': 'Siarad â pherson go iawn',
    'cta.switchNow': 'Newid nawr',
    'cta.comparePlans': 'Cymharu cynlluniau',
    'cta.getStarted': 'Dechrau arni',

    // Support
    'support.title': 'Rydym yma i helpu',
    'support.phone': 'Ffôn',
    'support.email': 'E-bost',
    'support.liveChat': 'Sgwrs Fyw',
    'support.available247': 'Llun–Gwener, 9yb–5yh',

    // Footer
    'footer.followUs': 'Dilynwch ni',
    'footer.stayConnected': 'Arhoswch yn gysylltiedig â ni ar y cyfryngau cymdeithasol',
    'footer.allRightsReserved': 'Cedwir pob hawl',

    // Switching Journey
    'switch.title': 'Newid i Direct Connect Cymru',
    'switch.subtitle': 'Mae newid yn hawdd ac yn ddi-bryder',
    'switch.step1': 'Gwirio argaeledd',
    'switch.step2': 'Dewis cynllun',
    'switch.step3': 'Rydym yn trin y newid',
    'switch.step4': 'Cael eich cysylltu',
    'switch.noInterruption': 'Dim Torri ar y Gwasanaeth',
    'switch.4gConnection': 'Byddwn yn darparu cysylltiad 4G lle bo\'n bosibl i gadw chi ar-lein yn ystod y newid',
    'switch.weHandleEverything': 'Byddwn yn trin popeth',
    'switch.supportAvailable': 'Cymorth Llun–Gwener, 9yb–5yh',

    // Coverage Section
    'coverage.title': 'CWMPAS',
    'coverage.heading': 'Ehangu ar draws Cymru',
    'coverage.description': 'Rydym yn ehangu ein rhwydwaith ffibr llawn yn barhaus ar draws Cymru. Gwiriwch eich cod post i weld a ydym ar gael yn eich ardal, neu gofrestrwch eich diddordeb ar gyfer cwmpas yn y dyfodol.',
    'coverage.postcodePlaceholder': 'Cod Post',
    'coverage.checkButton': 'Gwirio Cwmpas',
    'coverage.mapTitle': 'Map Rhyngweithiol',
    'coverage.mapComingSoon': 'Delweddu cwmpas byw yn dod yn fuan!',

    // Testimonials Section
    'testimonials.title': 'TYSTIOLAETH',
    'testimonials.heading': 'Ymddiriedir gan Gymru',
    'testimonials.subtitle': 'Gwelwch beth sydd gan ein cwsmeriaid i\'w ddweud am eu profiad gyda Direct Connect Cymru',

    // Help Section
    'help.title': 'CYMORTH',
    'help.heading': 'Rydym yma i helpu',
    'help.description': 'Cael atebion ar unwaith i gwestiynau cyffredin, gwirio statws gwasanaeth, neu gysylltu â\'n tîm cymorth.',
    'help.setupGuides': 'Canllawiau gosod',
    'help.outageStatus': 'Statws toriad',
    'help.speedTest': 'Rhedeg prawf cyflymder',
    'help.contactSupport': 'Cysylltu â chymorth',
    'help.needHelpNow': 'Angen help nawr?',
    'help.chatOrCallback': 'Sgwrsio gydag arbenigwr neu archebu galwad yn ôl.',
    'help.liveChat': 'Sgwrs Fyw',
    'help.bookCall': 'Archebu Galwad',

    // Packages Page
    'packagesPage.label': 'PECYNNAU',
    'packagesPage.title': 'Dewiswch Eich Pecyn Band Eang',
    'packagesPage.description': 'Atebion rhyngrwyd cyflym a dibynadwy ar gyfer cartrefi a busnesau ar draws Cymru.',
  }
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('language')
    return (saved === 'cy' ? 'cy' : 'en') as Language
  })

  useEffect(() => {
    localStorage.setItem('language', language)
    document.documentElement.lang = language
  }, [language])

  const setLanguage = (lang: Language) => {
    setLanguageState(lang)
  }

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations.en] || key
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  )
}

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}
